// Every page, in sidebar order: its path, label, section and who sees it. routes.tsx gives each one its icon and page
// (TypeScript refuses a path without one), and the sidebar, command palette and page switcher read the result.
// Plain data, so server tests can check it (server/test/projects.test.ts). A route without a page shows a
// "coming in phase N" placeholder; a `redirect` keeps an old address working (its query string comes along).
import type { Role } from "../../shared/mutations.ts";

export type NavDef = {
  path: string;
  label: string;
  section: string;
  phase?: number;
  blurb?: string;
  roles?: readonly Role[];
  /** An old address: not in the sidebar or palette, sent on to this path (plus its own query). */
  redirect?: string;
  /** Reached from another page (a Settings tab, the Bookkeeper pack on Bills), not a sidebar row; Ctrl+K still finds it. */
  sidebar?: false;
};

// Manufacturing projects --- Inventory (bought finished, sold) and Manufacturing (made from parts) are the two worlds;
// the Parts library is every material and sub-assembly across projects (ERP-PLAN §5.12). The sidebar is 15 rows in three
// sections (Inventory, Manufacturing, Money), Home on top and Settings at the bottom: it fits a 900 px screen.
export const NAV = [
  { path: "/", label: "Home", section: "" },

  // Inventory: things HHL buys finished and sells.
  { path: "/items/sell", label: "Sell items", section: "Inventory" },
  { path: "/forecast", label: "Forecast", section: "Inventory" }, // Forecasting ---
  { path: "/purchasing", label: "Purchasing plan", section: "Inventory" }, // Planning ---
  { path: "/pos", label: "Purchase orders", section: "Inventory" }, // Purchase orders ---
  // Receiving --- (the only page the receiving role sees; see canSee in lib/access.ts)
  { path: "/receiving", label: "Receiving", section: "Inventory", roles: ["admin", "inventory", "receiving"] },
  // Stock counts --- the day's list to count by hand; receivers count too (only admins see counts against the books)
  { path: "/counts", label: "Stock counts", section: "Inventory", roles: ["admin", "inventory", "receiving"] },
  { path: "/vendors", label: "Vendors", section: "Inventory" },

  // Manufacturing, around runs (Mitch, 2026-11-04: "a simplified manufacturing space where we focus on production runs and
  // the purchase orders that go with them"): a project is set up once (Configure: its BOMs, specs, sourcing), a run is
  // one batch at one CM (its units, orders, invoices, landed cost), the Make plan says when to run, Consignment is what
  // runs leave at the CM. Specs and BOMs live in a project's Configure (their own pages stay, off the sidebar: links,
  // Ctrl+K and old addresses still reach them).
  { path: "/projects", label: "Projects", section: "Manufacturing" },
  { path: "/runs", label: "Runs", section: "Manufacturing" }, // every project's runs
  { path: "/make", label: "Make plan", section: "Manufacturing" }, // Make planning ---
  // Consignment --- stock at each CM, make orders with readiness, shipments, counts, value
  { path: "/consignment", label: "Consignment", section: "Manufacturing" },
  { path: "/parts", label: "Parts library", section: "Manufacturing" },
  { path: "/specs", label: "Specs", section: "Manufacturing", sidebar: false }, // Specs --- specs and change notices
  { path: "/boms", label: "BOMs", section: "Manufacturing", sidebar: false }, // BOMs ---

  // Bills and payments --- the Payments due board, bills, payments, Wise history; the month's pack for the bookkeeper
  { path: "/bills", label: "Bills & payments", section: "Money" },
  { path: "/bookkeeper", label: "Bookkeeper pack", section: "Money", sidebar: false }, // a button on Bills

  // Settings: one sidebar row, a page of tabs (the pages under /settings/, each for the roles it names).
  { path: "/settings", label: "Settings", section: "Settings" },
  { path: "/settings/users", label: "Users", section: "Settings", roles: ["admin"], sidebar: false },
  { path: "/settings/shopify", label: "Shopify", section: "Settings", roles: ["admin"], sidebar: false },
  // PO email --- each person connects their Gmail; who POs are sent from
  { path: "/settings/email", label: "Email", section: "Settings", roles: ["admin", "inventory"], sidebar: false },
  { path: "/settings/wise", label: "Wise", section: "Settings", roles: ["admin", "inventory"], sidebar: false }, // Bills and payments ---
  // Which card --- HHL's own ways to pay: a name and the last 4 digits
  { path: "/settings/payment-accounts", label: "Payment accounts", section: "Settings", roles: ["admin", "inventory"], sidebar: false },
  { path: "/settings/mcp", label: "Claude access", section: "Settings", sidebar: false },

  // Old addresses: the Materials and Sub-assemblies pages are the Parts library now.
  { path: "/items/materials", label: "Materials", section: "Manufacturing", redirect: "/parts?kind=material" },
  // Product flow --- a project's tab now (Mitch, 2026-11-01: it only makes sense for a project's products)
  { path: "/flow", label: "Product flow", section: "Manufacturing", redirect: "/projects" },
  { path: "/items/subassemblies", label: "Sub-assemblies", section: "Manufacturing", redirect: "/parts?kind=subassembly" },
] as const satisfies readonly NavDef[];

export type NavPath = (typeof NAV)[number]["path"];

/** The route for a path: exact, else the longest one it's under ("/projects/GCU2" → "/projects"). */
export function matchNav<R extends { path: string }>(list: readonly R[], path: string): R | undefined {
  return (
    list.find((r) => r.path === path) ?? [...list].sort((a, b) => b.path.length - a.path.length).find((r) => r.path !== "/" && path.startsWith(`${r.path}/`))
  );
}

/** Where an old address goes now, with its own query on top of the target's ("?open=…" stays). */
export function redirectFor(def: Pick<NavDef, "path" | "redirect">, search: string): string | null {
  if (!def.redirect) return null;
  const [path, query = ""] = def.redirect.split("?");
  const params = new URLSearchParams(query);
  for (const [k, v] of new URLSearchParams(search)) params.set(k, v);
  const qs = params.toString();
  return `${path}${qs ? `?${qs}` : ""}`;
}

/** Shown in the palette: everything but old addresses. */
export const isListed = (def: Pick<NavDef, "path" | "redirect">) => !def.redirect;

/** A sidebar row: listed, and not a page reached from another (Settings' tabs, the Bookkeeper pack). */
export const inSidebar = (def: NavDef) => isListed(def) && def.sidebar !== false;

/** Settings' tabs, in order. */
export const SETTINGS_TABS: readonly NavDef[] = NAV.filter((n) => n.path.startsWith("/settings/"));
